import { Fragment, useCallback, useEffect, useState } from "react";
import Tooltip from "../../../../components/ui/Tooltip";
import IdentityUploadModal from "./modals/IdentityUploadModal";
import ApiResponseModal from "../../../../components/ui/popup/ApiResponseModal";
import {
  updateFilingIdentity,
  getFilingIdentity,
  saveFilingIdentity,
} from "../../../../api/filings/identityApi.js";
import { useParams } from "react-router-dom";
import { getUserRole } from "../../../../utils/auth.js";
import LoadingPlaceholder from "../../../../components/ui/loading/LoadingPlaceholder.js";
import { useRefreshOnAuth } from "../../../../hooks/useRefreshOnAuth.js";

function FilingIdentity() {
  const params = useParams();
  const filingUuid = params.filingUuid;
  const [identityData, setIdentityData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Result Modal States (Reusing your Avatar pattern)
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  const [resultMessage, setResultMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  const identity_title = "Identity Verify";
  const identity_description =
    "Tax Experts may require you to provide a valid id: State Id, Drive License or Passport while filing your return.";

  const userRole = getUserRole();
  const isCpa = userRole === "CPA";

  const fetchIdentity = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getFilingIdentity(filingUuid);

      if (response.ok) {
        const result = await response.json();
        if (Array.isArray(result) && result.length > 0) {
          setIdentityData(result[0]);
        } else {
          setIdentityData(null);
        }
      } else {
        setIdentityData(null);
      }
    } catch (error) {
      console.error("Error fetching identity:", error);
      setIdentityData(null);
    } finally {
      setIsLoading(false);
    }
  }, [filingUuid]);

  // This side effect is used for: (a) Refresh page (b) When render the page for first
  useRefreshOnAuth(fetchIdentity);

  useEffect(() => {
    if (filingUuid) fetchIdentity();
  }, [filingUuid, fetchIdentity]);

  const handleUploadSuccess = (newUrl) => {
    setIdentityData((prev) => ({ ...prev, identity: newUrl }));
    setIsSuccess(true);
    setResultMessage("Identity document updated successfully.");
    setIsResultModalOpen(true);
  };

  const handleUploadError = (message) => {
    setIsSuccess(false);
    setResultMessage(message);
    setIsResultModalOpen(true);
  };

  // Add this inside the FilingIdentity component
  const handleDownload = async (e) => {
    e.preventDefault(); // Prevent default link behavior

    try {
      const response = await fetch(identityData.identity);
      const blob = await response.blob();

      // Create a local URL for the blob object
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Set the file name (e.g., identity_uuid.jpg)
      const fileName =
        identityData.identity.split("/").pop() || `identity_${filingUuid}.jpg`;
      link.setAttribute("download", fileName);

      // Append to the document, click it, and remove it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the memory
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      // Fallback: if fetch fails (CORS), open in a new tab
      window.open(identityData.identity, "_blank");
    }
  };

  if (isLoading) return <LoadingPlaceholder rows={1} type="card-large" />;

  return (
    <Fragment>
      <div className="grey-box-card">
        <div className="grey-box-card-header">
          <h3>
            Upload Identifier&nbsp;
            <Tooltip title={identity_title} content={identity_description} />
          </h3>
        </div>

        <div className="identity-card-container">
          {/* Preview Logic */}
          {identityData?.identity ? (
            <div
              className="identity-card-preview-active"
              style={{ backgroundImage: `url(${identityData.identity})` }}
            />
          ) : (
            <div className="identity-card-preview-default" />
          )}

          <div className="identity-card-actions">
            {/* ONLY show Upload/Update if the user is NOT a CPA */}
            {!isCpa && (
              <button
                className="update-image-link"
                onClick={() => setIsModalOpen(true)}
              >
                <img
                  src="/images/icons/upload_blue.svg"
                  alt="Upload"
                  className="upload-icon"
                />
                {identityData ? "Update" : "Upload"}
              </button>
            )}

            {/* ALWAYS show Download for everyone if data exists */}
            {identityData?.identity && (
              <button
                onClick={handleDownload}
                className="update-image-link"
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <img
                  src="/images/icons/download_blue_light.svg"
                  alt="Download"
                  className="upload-icon"
                />
                Download
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Logic Components */}
      <IdentityUploadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        idUrl={identityData?.identity}
        uploadFunction={(file) =>
          // Pass filingUuid for the POST route, and identityData.id for the PUT route
          saveFilingIdentity(file, filingUuid, identityData?.id)
        }
        onUploadSuccess={handleUploadSuccess}
        onUploadError={handleUploadError}
      />

      <ApiResponseModal
        isOpen={isResultModalOpen}
        isSuccess={isSuccess}
        message={resultMessage}
        onClose={() => setIsResultModalOpen(false)}
        onRetry={() => {
          setIsResultModalOpen(false);
          setIsModalOpen(true);
        }}
      />
    </Fragment>
  );
}

export default FilingIdentity;
